import {
  type FloatApiServiceModelDescriptor,
  type IntegerApiServiceModelDescriptor,
  type ListApiServiceModelDescriptor,
  type Member,
  ModelType,
  type StringApiServiceModelDescriptor,
  type StructureApiServiceModelDescriptor,
  type TimestampApiServiceModelDescriptor,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import { DateTime } from "luxon";
import { z } from "zod";

export function getInitialValueForModel(model: UnwrappedApiServiceModelDescriptor) {
  return generateParameterValidationSchema(model, true).parse(undefined);
}

export function sanitizePattern(pattern: string): [string, string] {
  let sanitizedPattern = pattern;
  let flags: string = "";
  // javascript regex does not support (?s) flag; use s flag instead
  if (pattern.includes("(?s)")) {
    flags += "s";
    sanitizedPattern = pattern.replace(/\(\?s\)/g, "");
  }
  // if pattern contains \p or \P, it means that it is a unicode regex
  if (/\\[pP]{[^}]+}/.test(pattern)) {
    flags += "u";
  }
  return [sanitizedPattern, flags];
}

function createErrorMessage(label: string | string[] | undefined, type: string) {
  if (!label) return undefined;

  return {
    required_error: `${label} is required`,
    invalid_type_error: `${label} must be a ${type}`,
  };
}

function getStringSchema({
  model,
  label,
  isRequired,
}: {
  model: StringApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  if (model.enum !== undefined) {
    const schema = z.enum(model.enum as [string, ...string[]]);

    return isRequired ? schema : schema.optional();
  }

  let schema = z.string(createErrorMessage(label, "string"));

  if (model.minLength !== undefined) {
    schema = schema.max(model.minLength);
  }
  if (model.maxLength !== undefined) {
    schema = schema.max(model.maxLength);
  }
  if (model.pattern !== undefined) {
    const [sanitizedPattern, flags] = sanitizePattern(model.pattern);
    schema = schema.regex(new RegExp(sanitizedPattern, flags));
  }

  return isRequired ? schema : schema.optional();
}

function getBooleanSchema({ label, isRequired }: { label: string | undefined; isRequired?: boolean }) {
  const schema = z.boolean(createErrorMessage(label, "boolean"));

  return isRequired ? schema : schema.optional();
}

function getFloatSchema({
  model,
  label,
  isRequired,
}: {
  model: FloatApiServiceModelDescriptor | IntegerApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  const schema = z
    .number(createErrorMessage(label, "float"))
    .refine(
      (n) => !z.number().int().safeParse(n).success && z.number().finite().safeParse(n).success,
      "should not be an integer"
    );

  const schemaWithMinCheck = model.min !== undefined ? schema.pipe(z.number().gte(model.min)) : schema;

  const schemaWithBothChecks =
    model.max !== undefined ? schemaWithMinCheck.pipe(z.number().lte(model.max)) : schemaWithMinCheck;

  return isRequired ? schemaWithBothChecks : schemaWithBothChecks.optional();
}

function getIntegerSchema({
  model,
  label,
  isRequired,
}: {
  model: IntegerApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  let schema = z.number(createErrorMessage(label, "integer")).int();

  if (model.min !== undefined) {
    schema = schema.max(model.min);
  }
  if (model.max !== undefined) {
    schema = schema.max(model.max);
  }

  return isRequired ? schema : schema.optional();
}

function getTimestampSchema({
  model,
  label,
  isRequired,
}: {
  model: TimestampApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  // default is milliseconds
  if (model.timestampFormat === undefined) {
    const schema = z
      .number(createErrorMessage(label, "timestamp"))
      .int()
      .refine((v) => {
        const t = DateTime.fromMillis(v);
        return t.isValid && t.year >= 1970 && t.year <= 2100;
      }, "value is not a valid millisecond timestamp");

    return isRequired ? schema : schema.optional();
  }

  switch (model.timestampFormat) {
    case "x": {
      // millisecond
      const schema = z
        .number(createErrorMessage(label, "timestamp"))
        .int()
        .refine((v) => {
          const t = DateTime.fromMillis(v);
          return t.isValid && v >= 100000000000 && t.year >= 1970 && t.year <= 2100;
        }, "value is not a valid millisecond timestamp");

      return isRequired ? schema : schema.optional();
    }
    case "X": {
      const schema = z
        .number(createErrorMessage(label, "timestamp"))
        .int()
        .refine((v) => {
          const t = DateTime.fromSeconds(v);
          return t.isValid && t.year >= 1970 && t.year <= 2100;
        }, "value is not a valid second timestamp");

      return isRequired ? schema : schema.optional();
    }
    default: {
      const schema = z.string(createErrorMessage(label, "timestamp"));
      return isRequired ? schema : schema.optional();
    }
  }
}

function getListSchema({
  model,
  label,
  isRequired,
}: {
  model: ListApiServiceModelDescriptor<Member>;
  label: string | undefined;
  isRequired?: boolean;
}) {
  let schema = generateParameterValidationSchema(model.member.model, isRequired).array();

  if (model.min !== undefined) {
    schema = schema.min(model.min, { message: `${label || "list"} must have at least ${model.min} items` });
  }
  if (model.max !== undefined) {
    schema = schema.max(model.max, { message: `${label || "list"} must have at most ${model.max} items` });
  }

  return isRequired ? schema : schema.optional();
}

function getStructureSchema({
  model,
  label,
  isRequired,
}: {
  model: StructureApiServiceModelDescriptor<Member>;
  label: string | undefined;
  isRequired?: boolean;
}) {
  const schema = z
    .object(
      Object.fromEntries(
        Object.entries(model.members).map(([memberName, member]) => [
          memberName,
          generateParameterValidationSchema(member.model, model.requiredMembers?.includes(memberName), memberName),
        ])
      ),
      createErrorMessage(label, "structure")
    )
    .strict();

  return isRequired ? schema : schema.optional();
}

export function generateParameterValidationSchema(
  model: UnwrappedApiServiceModelDescriptor,
  isRequired?: boolean,
  label?: string
) {
  let schema: z.ZodTypeAny;

  switch (model.type) {
    case ModelType.STRING: {
      schema = getStringSchema({ model, label, isRequired });
      break;
    }
    case ModelType.BOOLEAN: {
      schema = getBooleanSchema({ label, isRequired });
      break;
    }
    case ModelType.INTEGER: {
      schema = getIntegerSchema({ model, label, isRequired });
      break;
    }
    case ModelType.FLOAT: {
      schema = getFloatSchema({ model, label, isRequired });
      break;
    }
    case ModelType.TIMESTAMP: {
      schema = getTimestampSchema({ model, label, isRequired });
      break;
    }
    case ModelType.LIST: {
      schema = getListSchema({ model, label, isRequired });
      break;
    }
    case ModelType.STRUCTURE: {
      schema = getStructureSchema({ model, label, isRequired });
      break;
    }
    default:
      throw new Error(`Schema generation for model type ${model.type} is not implemented yet.`);
  }

  return schema;
}
